import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

// 사진 전체 보기 — Modal(size="full") + 이전·다음 + 카운터 (WP5 4단계 U0-4).
// 확대·이동 기능 없음. 색은 토큰 표면(.modal-box)을 따름. contract 없는 손 작성 컴포넌트(SignalDot과 동일 예외).
export interface PhotoViewerItem {
  /** 항목 식별자(선택) — 있으면 렌더 key 로 사용. */
  id?: string | number;
  src: string;
  alt?: string;
}

export interface PhotoViewerProps {
  open: boolean;
  onClose: () => void;
  items: PhotoViewerItem[];
  /** 현재 사진 인덱스(0 기준). */
  index: number;
  onIndexChange: (index: number) => void;
  /** 모달 제목(선택). 없으면 접근성 이름만 "사진 보기". */
  title?: string;
}

export function PhotoViewer({ open, onClose, items, index, onIndexChange, title }: PhotoViewerProps) {
  const count = items.length;
  const safeIndex = count === 0 ? 0 : Math.min(Math.max(index, 0), count - 1);
  const current = items[safeIndex];
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const go = (delta: number) => {
    if (count < 2) return;
    onIndexChange((safeIndex + delta + count) % count);
  };

  // ←→ 키 이동 (Esc·포커스 트랩은 Modal 담당)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, safeIndex, count]);

  return (
    <Modal open={open} onClose={onClose} size="full" title={title} aria-label="사진 보기">
      <div className="flex h-full flex-col gap-2">
        <div className="flex min-h-0 flex-1 items-center justify-center">
          {current && failedSrc === current.src ? (
            <p>사진을 불러오지 못했어요</p>
          ) : current ? (
            <img
              key={current.id ?? current.src}
              src={current.src}
              alt={current.alt ?? ''}
              className="max-h-full max-w-full object-contain"
              onError={() => setFailedSrc(current.src)}
            />
          ) : null}
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button variant="secondary" aria-label="이전 사진" disabled={count < 2} onClick={() => go(-1)}>‹</Button>
          <span aria-live="polite">{count === 0 ? '0 / 0' : `${safeIndex + 1} / ${count}`}</span>
          <Button variant="secondary" aria-label="다음 사진" disabled={count < 2} onClick={() => go(1)}>›</Button>
          <Button variant="ghost" onClick={onClose}>닫기</Button>
        </div>
      </div>
    </Modal>
  );
}
